import { Alert, Button, Card, CardContent, TextField } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import {
  MAX_MESSAGE_FOOTER,
  type PublishingMessageSettings,
} from '../../../api/src/telegram/publishing.types';
import { api } from '../api/client';
import { Stack, Typography } from '../components/mui-system';

export function PublishingMessageSettingsForm({
  workspaceId,
  value,
  canEdit,
  onChanged,
}: {
  workspaceId: string;
  value: PublishingMessageSettings;
  canEdit: boolean;
  onChanged: () => Promise<void>;
}) {
  const [draft, setDraft] = useState<PublishingMessageSettings | null>(null);
  const [busy, setBusy] = useState(false),
    [error, setError] = useState('');
  const requestId = useRef(0);
  useEffect(
    () => () => {
      requestId.current++;
    },
    [],
  );
  async function save() {
    if (!draft) return;
    const request = ++requestId.current;
    setBusy(true);
    setError('');
    try {
      await api.publishingMessageSettings(workspaceId, draft);
      if (request !== requestId.current) return;
      await onChanged();
      if (request === requestId.current) setDraft(null);
    } catch (e) {
      if (request === requestId.current)
        setError(e instanceof Error ? e.message : 'Не удалось сохранить футер');
    } finally {
      if (request === requestId.current) setBusy(false);
    }
  }
  return (
    <Card variant="outlined">
      <CardContent>
        <Stack gap={2}>
          <Typography variant="h6">Футер публикаций</Typography>
          <Typography color="text.secondary">
            Общий для всех каналов рабочего пространства. Добавляется после имени, возраста и
            города. Прямая ссылка на анкету в подпись не вставляется.
          </Typography>
          {canEdit ? (
            <>
              <TextField
                label="Текст футера"
                multiline
                minRows={2}
                value={draft?.footer ?? value.footer}
                disabled={busy}
                onChange={(e) => setDraft({ ...(draft ?? value), footer: e.target.value })}
                slotProps={{ htmlInput: { maxLength: MAX_MESSAGE_FOOTER } }}
                helperText={`До ${MAX_MESSAGE_FOOTER} символов. Обычный текст, без шаблонов. Пустое поле убирает футер.`}
              />
              {draft && draft.revision !== value.revision && (
                <Alert severity="warning">
                  Сохранённый футер изменился. Загрузите его перед редактированием.
                </Alert>
              )}
              {error && <Alert severity="error">{error}</Alert>}
              <Stack direction="row" gap={1} flexWrap="wrap">
                <Button
                  variant="outlined"
                  disabled={busy || !draft || draft.revision !== value.revision}
                  onClick={() => void save()}
                >
                  {busy ? 'Сохранение…' : 'Сохранить футер'}
                </Button>
                {draft && (
                  <Button
                    disabled={busy}
                    onClick={() => {
                      setDraft(null);
                      setError('');
                    }}
                  >
                    Загрузить сохранённый футер
                  </Button>
                )}
              </Stack>
            </>
          ) : (
            <Typography sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
              {value.footer || 'Без футера'}
            </Typography>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
}
